/* eslint-disable no-undef */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withRouter } from 'react-router-dom';
import styled from '@emotion/styled';
import { SPACING } from 'Style/spacing';
import { BREAKPOINTS } from 'Style/breakpoints';
import { CONTENT_BODY, CONTENT_STORY_HEADING_TYPES } from 'Style/typography';
import { TEXT_COLORS, DefaultTextColorStyle } from 'Style/colors';

/* Utils */
import StoryboardRenderingUtil from 'Utils/content/storyboard-rendering-util';
import DateUtil from 'Utils/date-util';
import linkifyText from 'Utils/linkify-text';

/* Components */
import WebLink from '../../components/web-link';
import StoryboardAuthorAttribution from './author-attribution';
import StoryboardHeaderCoverImage from './header/cover-image';
import TopicTags from '../../components/item/topic-tags';
import ItemSocialActions, {
  StyledMetrics,
} from '../../components/item/item-social-actions';
import Badge from '../../components/badge';

import { StyledItemMetric } from 'Webapp/shared/app/components/item-actions/item-metric';
import DefaultItemActionButton from 'Webapp/shared/app/components/item-actions/default-item-action-button';
import { Meatball } from 'ComponentLibrary/icons/meatball';

import withFollowAuthor from '../../hocs/withFollowAuthor';
import withT from 'ComponentLibrary/hocs/withT';
import withBreadcrumbs from '../../hocs/withBreadcrumbs';

import connector from 'Utils/connector';
import connectResponsive from 'Webapp/shared/app/connectors/connectResponsive';
import connectLanguage from 'Webapp/shared/app/connectors/connectLanguage';
import connectGetSection from 'Webapp/shared/app/connectors/connectGetSection';
import connectAmp from 'Webapp/shared/app/connectors/connectAmp';
import connectFeatureFlags from 'Webapp/shared/app/connectors/connectFeatureFlags';

const shouldForwardProp = (prop) => prop !== 'isInline';
const themedStoryboardHeaderStyle = {
  backgroundColor: 'var(--color--surface-new-storyboard-default-cover)', // Overridden in code by cover item
  color: TEXT_COLORS.overlay,

  '.storyboard-header__description': {
    color: TEXT_COLORS.overlay,
  },

  '.topic-tag': {
    color: TEXT_COLORS.overlay,
  },
  'a, a:hover': {
    color: TEXT_COLORS.overlay,
  },
  '.button--base': {
    color: TEXT_COLORS.overlay,
  },

  '.item-social-actions': {
    color: 'unset',
  },
};
const StyledItemSocialActions = styled(ItemSocialActions, {
  shouldForwardProp,
})(({ isInline }) => ({
  [DefaultItemActionButton]: DefaultTextColorStyle(!isInline, false),
  [Meatball]: {
    backgroundColor: isInline ? TEXT_COLORS.primary : TEXT_COLORS.overlay,
  },

  [StyledItemMetric]: DefaultTextColorStyle(!isInline),
  '.social-actions': {
    backgroundColor: 'inherit',
  },

  [StyledMetrics]: {
    color: isInline ? TEXT_COLORS.primary : TEXT_COLORS.overlay,
  },

  '.social-actions__content': {
    backgroundColor: 'inherit',
  },
}));

const StoryboardTitle = styled('h1', { shouldForwardProp })(
  CONTENT_STORY_HEADING_TYPES.LARGE,
  BREAKPOINTS.phone(CONTENT_STORY_HEADING_TYPES.LARGE_OVERLAY),
  ({ isInline }) => ({
    ...DefaultTextColorStyle(!isInline),
  }),
);
const inlineStoryboardHeaderStyle = {
  '.share-icon, .flip-icon, .like-icon': {
    color: TEXT_COLORS.primary,
  },
  '.meatballs-menu-icon__meatball': {
    backgroundColor: TEXT_COLORS.primary,
  },
  '.topic-tag': {
    color: TEXT_COLORS.primary,
  },
};

const applyHeaderVariant = function (props) {
  return props.isInline
    ? inlineStoryboardHeaderStyle
    : themedStoryboardHeaderStyle;
};
const HeaderDiv = styled('div', { shouldForwardProp })(
  {
    display: 'grid',
  },
  BREAKPOINTS.phone({
    gridTemplateColumns: '1fr',
    gridTemplateRows: 'auto max-content auto',
    bottom: '100px',
  }),
  BREAKPOINTS.tabletPortraitUp({
    gridTemplateColumns: '1fr 1fr',
  }),
  applyHeaderVariant,
);

const inlineHeaderContentStyle = {
  marginLeft: 'unset',
  paddingLeft: 'unset',
  maxWidth: 'unset',
};
const themedStoryboardHeaderContentStyle = {
  paddingLeft: SPACING.LARGE,
  '@media (min-width: 496px)': {
    marginLeft: 'auto',
    maxWidth: '480px',
  },
};
const applyHeaderContentVariant = function (props) {
  return props.isInline
    ? inlineHeaderContentStyle
    : themedStoryboardHeaderContentStyle;
};
const HeaderContentDiv = styled('div', { shouldForwardProp })(
  {
    width: '100%',
    paddingRight: SPACING.LARGE,
  },
  BREAKPOINTS.tabletLandscapeUp({
    paddingRight: SPACING.BASE6X,
  }),
  applyHeaderContentVariant,
);

const BadgeHeaderDiv = styled(HeaderContentDiv)(
  {
    color: TEXT_COLORS.overlay,
    gridColumn: '1 / 2',
    gridRow: '1 / 2',
  },
  BREAKPOINTS.phone({
    display: 'flex',
    alignItems: 'flex-end',
    paddingTop: SPACING.BASE,
    paddingBottom: SPACING.BASE,
    zIndex: '3',
  }),
  BREAKPOINTS.tablet({
    paddingTop: SPACING.XLARGE,
    paddingBottom: SPACING.BASE4X,
  }),
  BREAKPOINTS.desktopUp({
    paddingTop: SPACING.BASE6X,
    paddingBottom: SPACING.BASE4X,
  }),
);

const HeaderTitleDiv = styled(HeaderContentDiv)(
  {
    mixBlendMode: 'normal',
    gridColumn: '1 / 2',
    gridRow: '2 / 3',
  },
  BREAKPOINTS.phone({
    display: 'flex',
    alignItems: 'flex-end',
    paddingBottom: SPACING.BASE6X,
    zIndex: '3',
  }),
);
const HeaderBodyDiv = styled(HeaderContentDiv)(
  {
    gridColumn: '1 / 2',
    gridRow: '3 / 4',
    paddingBottom: SPACING.LARGE,
  },
  BREAKPOINTS.tabletPortraitUp({
    paddingBottom: SPACING.BASE4X,
  }),
  BREAKPOINTS.desktopUp({
    paddingBottom: SPACING.BASE8X,
  }),
);

const HeaderDescription = styled.p(
  CONTENT_BODY,
  {
    marginTop: SPACING.LARGE,
    marginBottom: SPACING.BASE4X,
  },
  BREAKPOINTS.tabletPortraitUp({
    marginBottom: SPACING.BASE6X,
  }),
);

const HeaderTagsList = styled.ul({
  marginBottom: SPACING.BASE4X,
  a: { marginTop: SPACING.SMALL },
});

class StoryboardHeader extends Component {
  constructor(props) {
    super(props);

    this.state = {
      loadingAuthorSection: false,
    };
    this.renderHeaderContent = this.renderHeaderContent.bind(this);
    this.setBreadcrumbs = this.setBreadcrumbs.bind(this);
  }

  componentDidMount() {
    this.setBreadcrumbs();
  }

  componentDidUpdate() {
    const { authorSectionId, authorSection, getSection } = this.props;
    const { loadingAuthorSection } = this.state;
    if (!authorSection && authorSectionId && !loadingAuthorSection) {
      this.setState(
        {
          loadingAuthorSection: true,
        },
        () =>
          getSection(authorSectionId).then(() =>
            this.setState({ loadingAuthorSection: false }),
          ),
      );
    }
    this.setBreadcrumbs();
  }

  setBreadcrumbs() {
    const { coverItem, section, setBreadcrumbs, isInline } = this.props;
    if (!isInline) {
      setBreadcrumbs({
        item: coverItem,
        section,
      });
    }
  }

  renderHeaderContent() {
    const {
      section,
      coverItem,
      authorSection,
      displayCoverAttribution,
      isAnalytics,
      lang,
      featureFlags,
      isInline,
      isAmp,
      t,
    } = this.props;
    const { description } = section;
    const headerDescriptionOverride =
      StoryboardRenderingUtil.headerDescriptionOverride(coverItem);

    const descriptionToRender = headerDescriptionOverride || description;
    const linkifiedDescription =
      descriptionToRender &&
      linkifyText(
        descriptionToRender,
        { name: 'storyboard-description-link' },
        WebLink,
      );

    const StoryboardAttribution = styled(StoryboardAuthorAttribution)(
      {
        marginTop: SPACING.LARGE,
        marginBottom: SPACING.BASE6X,
      },
      BREAKPOINTS.tabletPortraitUp({ marginBottom: SPACING.BASE4X }),
    );

    return (
      <HeaderBodyDiv
        isInline={isInline}
        className="storyboard-header__content storyboard-header__content-body"
      >
        <div>
          {linkifiedDescription && (
            <HeaderDescription className="storyboard-header__description">
              {linkifiedDescription}
            </HeaderDescription>
          )}
          {section && displayCoverAttribution && (
            <StoryboardAttribution
              section={section}
              coverItem={coverItem}
              authorSection={authorSection}
            />
          )}
          <HeaderTagsList>
            <TopicTags topics={coverItem && coverItem.topics} />
          </HeaderTagsList>
          {coverItem && !isAmp && (
            <StyledItemSocialActions
              item={coverItem}
              section={section}
              isLikedItem={coverItem.isLikedItem}
              articleRemoteId={coverItem.articleRemoteId}
              isAnalytics={isAnalytics}
              isInline={isInline}
            />
          )}
        </div>
        {isAnalytics && (
          <div className="ui-text--body-2 storyboard-header__metadata-wrapper">
            <p>
              {t('analytics_storyboard_published')}{' '}
              {coverItem && coverItem.dateCreated && (
                <time>
                  {DateUtil.friendlyFormatLocalized(
                    coverItem.dateCreated,
                    t,
                    lang,
                  )}
                </time>
              )}
            </p>
            {featureFlags.INTERNAL_PACKAGE_MANAGEMENT && (
              <p>ID: {section.storyboardDefinitionId}</p>
            )}
          </div>
        )}
      </HeaderBodyDiv>
    );
  }

  render() {
    const {
      section,
      coverItem,
      displayCoverAttribution,
      location,
      isAmp,
      isInline,
      t,
    } = this.props;

    const { title } = section;

    const coverImageSquare =
      coverItem && StoryboardRenderingUtil.headerImageSquare(coverItem);
    const coverImage = StoryboardRenderingUtil.getCoverImage(
      coverItem,
      section,
    );

    const hideCoverImage =
      isAmp ||
      (coverItem && StoryboardRenderingUtil.isHiddenHeaderImage(coverItem));

    const headerClassName = classNames({
      'storyboard-header': true,
      'storyboard-header--hide-cover-image': hideCoverImage,
    });

    const titleElement = (
      <StoryboardTitle isInline={isInline}>{title}</StoryboardTitle>
    );

    const backgroundStyle = isInline
      ? {}
      : StoryboardRenderingUtil.getBackgroundColorStyle(coverImage, 0);

    return (
      <HeaderDiv
        isInline={isInline}
        className={headerClassName}
        style={backgroundStyle}
      >
        {/* LEFT COLUMN */}{' '}
        {!isInline && (
          <BadgeHeaderDiv
            isInline={isInline}
            className="storyboard-header__content"
          >
            <Badge isOverlay>{t('storyboard')}</Badge>
          </BadgeHeaderDiv>
        )}
        <HeaderTitleDiv
          isInline={isInline}
          className="storyboard-header__content"
        >
          {location.pathname === section.canonicalPath ? (
            titleElement
          ) : (
            <WebLink href={section.canonicalPath}>{titleElement}</WebLink>
          )}
        </HeaderTitleDiv>
        {this.renderHeaderContent()}
        {/* TODO: Do we need this? */}
        {/* {isLargeScreen && <SponsoredAuthor section={section} />} */}
        {/* </div> */}
        {/* RIGHT COLUMN */}
        {!hideCoverImage && (
          <StoryboardHeaderCoverImage
            section={section}
            coverImage={coverImage}
            coverImageSquare={coverImageSquare}
            displayCoverAttribution={displayCoverAttribution}
          />
        )}
        {/* TODO: Do we need this? */}
        {/* {!isLargeScreen && <SponsoredAuthor section={section} />} */}
      </HeaderDiv>
    );
  }
}

StoryboardHeader.propTypes = {
  section: PropTypes.object.isRequired,
  coverItem: PropTypes.object,
  location: PropTypes.object.isRequired,
  lang: PropTypes.string.isRequired,
  isAmp: PropTypes.bool,
  isPhone: PropTypes.bool.isRequired,
  featureFlags: PropTypes.object.isRequired,
  authorSectionId: PropTypes.string,
  getSection: PropTypes.func.isRequired,
  authorSection: PropTypes.object,
  isAnalytics: PropTypes.bool,
  displayCoverAttribution: PropTypes.bool,
  t: PropTypes.func.isRequired,
  setBreadcrumbs: PropTypes.func.isRequired,
  isInline: PropTypes.bool,
};

StoryboardHeader.defaultProps = {
  coverItem: undefined,
  authorSectionId: null,
  authorSection: null,
  isAnalytics: false,
  displayCoverAttribution: true,
  isInline: false,
};

export default connector(
  connectAmp,
  connectGetSection,
  connectResponsive,
  connectLanguage,
  connectFeatureFlags,
)(withT(withRouter(withFollowAuthor(withBreadcrumbs(StoryboardHeader)))));
